import { z } from 'zod';


/**
 * Schema for employee contract edit form
 * Similar to create but contractNumber is a number (readonly in UI)
 * Validates all 12 fields according to ASIS-4190 requirements
 */
export const updateEmployeeContractSchema = z
  .object({
    // Contract basic info - contractNumber is number (not editable in UI)
    contractNumber: z
      .number({
        required_error: 'employee:contracts.validation.contractNumberRequired',
        invalid_type_error: 'employee:contracts.validation.contractNumberRequired',
      })
      .int()
      .positive(),
    contractName: z
      .string()
      .trim()
      .min(1, 'employee:contracts.validation.contractNameRequired')
      .max(200, 'employee:contracts.validation.contractNameMaxLength'),
    contractStatusId: z
      .number({
        required_error: 'employee:contracts.validation.contractStatusRequired',
        invalid_type_error: 'employee:contracts.validation.contractStatusRequired',
      })
      .int()
      .positive(),
    contractTypeId: z
      .number({
        required_error: 'employee:contracts.validation.contractTypeRequired',
        invalid_type_error: 'employee:contracts.validation.contractTypeRequired',
      })
      .int()
      .positive()
      .optional()
      .refine((value) => value !== undefined && value > 0, {
        message: 'employee:contracts.validation.contractTypeRequired',
      }),

    // Dates (as strings in yyyy-mm-dd format from DatePicker)
    dateEntry: z
      .string({
        required_error: 'employee:contracts.validation.dateEntryRequired',
        invalid_type_error: 'employee:contracts.validation.dateEntryRequired',
      })
      .min(1, 'employee:contracts.validation.dateEntryRequired')
      .refine(
        (value) => /^\d{4}-\d{2}-\d{2}$/.test(value),
        { message: 'employee:contracts.validation.invalidDateFormat' },
      ),
    contractStartDate: z
      .string({
        required_error: 'employee:contracts.validation.contractStartDateRequired',
        invalid_type_error: 'employee:contracts.validation.contractStartDateRequired',
      })
      .min(1, 'employee:contracts.validation.contractStartDateRequired')
      .refine(
        (value) => /^\d{4}-\d{2}-\d{2}$/.test(value),
        { message: 'employee:contracts.validation.invalidDateFormat' },
      ),
    contractEndDate: z
      .string()
      .refine(
        (value) => !value || /^\d{4}-\d{2}-\d{2}$/.test(value),
        { message: 'employee:contracts.validation.invalidDateFormat' },
      )
      .nullable()
      .optional()
      .transform((value) => (value === '' || !value ? null : value)),

    // Organization
    contractModalityId: z
      .number({
        required_error: 'employee:contracts.validation.contractModalityRequired',
        invalid_type_error: 'employee:contracts.validation.contractModalityRequired',
      })
      .int()
      .positive()
      .optional()
      .refine((value) => value !== undefined && value > 0, {
        message: 'employee:contracts.validation.contractModalityRequired',
      }),
    companyContractId: z
      .number({
        required_error: 'employee:contracts.validation.companyContractRequired',
        invalid_type_error: 'employee:contracts.validation.companyContractRequired',
      })
      .int()
      .positive()
      .optional()
      .refine((value) => value !== undefined && value > 0, {
        message: 'employee:contracts.validation.companyContractRequired',
      }),
    areaId: z
      .number({
        required_error: 'employee:contracts.validation.areaRequired',
        invalid_type_error: 'employee:contracts.validation.areaRequired',
      })
      .int()
      .positive()
      .optional()
      .refine((value) => value !== undefined && value > 0, {
        message: 'employee:contracts.validation.areaRequired',
      }),
    positionId: z
      .number({
        required_error: 'employee:contracts.validation.positionRequired',
        invalid_type_error: 'employee:contracts.validation.positionRequired',
      })
      .int()
      .positive()
      .optional()
      .refine((value) => value !== undefined && value > 0, {
        message: 'employee:contracts.validation.positionRequired',
      }),
    costCenterId: z
      .number({
        required_error: 'employee:contracts.validation.costCenterRequired',
        invalid_type_error: 'employee:contracts.validation.costCenterRequired',
      })
      .int()
      .positive()
      .optional()
      .refine((value) => value !== undefined && value > 0, {
        message: 'employee:contracts.validation.costCenterRequired',
      }),
    unionCode: z
      .string({
        required_error: 'employee:contracts.validation.unionRequired',
        invalid_type_error: 'employee:contracts.validation.unionRequired',
      })
      .min(1, 'employee:contracts.validation.unionRequired'),
  })
  .superRefine((data, context) => {
    // Validate dates: contractEndDate >= contractStartDate
    if (data.contractEndDate && data.contractStartDate) {
      const startDate = new Date(data.contractStartDate);
      const endDate = new Date(data.contractEndDate);

      if (endDate < startDate) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['contractEndDate'],
          message: 'employee:contracts.validation.endDateAfterStartDate',
        });
      }
    }

    // Validate: dateEntry <= contractStartDate
    if (data.dateEntry && data.contractStartDate) {
      const entryDate = new Date(data.dateEntry);
      const startDate = new Date(data.contractStartDate);

      if (entryDate > startDate) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['dateEntry'],
          message: 'employee:contracts.validation.entryDateBeforeStartDate',
        });
      }
    }

    // Validate: dateEntry <= contractEndDate
    if (data.contractEndDate && data.dateEntry) {
      const entryDate = new Date(data.dateEntry);
      const endDate = new Date(data.contractEndDate);

      if (entryDate > endDate) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['dateEntry'],
          message: 'employee:contracts.validation.entryDateBeforeEndDate',
        });
      }
    }
  });

export type UpdateEmployeeContractFormData = z.infer<typeof updateEmployeeContractSchema>;
